import { IdentityError, type IdentityService, type PlayerProfile } from '../network/IdentityService';
import { SocialService, type DirectMessage, type FriendContact, type SocialOverview } from '../network/SocialService';
import { crewInviteCode } from './CrewInvite';
import { friendUnreadStatus, presentFriendPresence } from './FriendPresence';
import { socialNotificationSummary } from './SocialNotification';

export class SocialFlow {
  private readonly panel: HTMLElement;
  private readonly openButtons: NodeListOf<HTMLButtonElement>;
  private readonly friendCallTag: HTMLInputElement;
  private readonly addButton: HTMLButtonElement;
  private readonly contacts: HTMLElement;
  private readonly requests: HTMLElement;
  private readonly conversationTitle: HTMLElement;
  private readonly messages: HTMLElement;
  private readonly composer: HTMLInputElement;
  private readonly sendButton: HTMLButtonElement;
  private readonly removeButton: HTMLButtonElement;
  private readonly error: HTMLElement;
  private readonly service: SocialService;
  private overview: SocialOverview = { friends: [], incoming: [], outgoing: [] };
  private selected: FriendContact | null = null;
  private history: DirectMessage[] = [];
  private polling = 0;
  private overviewPolling = 0;
  private busy = false;
  private messagePollBusy = false;
  private overviewBusy = false;
  private available = false;

  public constructor(root: HTMLElement, config: ConstructorParameters<typeof SocialService>[0], private readonly identity: IdentityService) {
    this.service = new SocialService(config, identity);
    this.panel = required(root, '[data-social]');
    this.openButtons = root.querySelectorAll('[data-social-open]');
    this.friendCallTag = required(root, '[data-friend-call-tag]');
    this.addButton = required(root, '[data-friend-add]');
    this.contacts = required(root, '[data-friend-list]');
    this.requests = required(root, '[data-friend-requests]');
    this.conversationTitle = required(root, '[data-conversation-title]');
    this.messages = required(root, '[data-direct-messages]');
    this.composer = required(root, '[data-direct-message]');
    this.sendButton = required(root, '[data-direct-send]');
    this.removeButton = required(root, '[data-friend-remove]');
    this.error = required(root, '[data-social-error]');

    for (const button of this.openButtons) button.addEventListener('click', () => void this.open());
    this.addButton.addEventListener('click', () => void this.addFriend());
    this.friendCallTag.addEventListener('keydown', (event) => { if (event.key === 'Enter') void this.addFriend(); });
    this.sendButton.addEventListener('click', () => void this.send());
    this.composer.addEventListener('keydown', (event) => { if (event.key === 'Enter') void this.send(); });
    this.removeButton.addEventListener('click', () => void this.removeSelected());
    this.identity.subscribe(this.renderIdentity);
  }

  private readonly renderIdentity = (profile: PlayerProfile | null): void => {
    const available = profile?.guest === false;
    for (const button of this.openButtons) button.hidden = !available;
    if (available && !this.available) {
      this.available = true;
      void this.refreshOverview(true);
      this.startOverviewPolling();
    } else if (!available) {
      this.available = false;
      this.selected = null;
      this.history = [];
      this.composer.value = '';
      this.friendCallTag.value = '';
      this.overview = { friends: [], incoming: [], outgoing: [] };
      this.stopPolling();
      this.stopOverviewPolling();
      this.renderContacts();
      this.renderConversation();
    }
  };

  private async open(): Promise<void> {
    if (this.identity.current()?.guest !== false) return;
    this.error.textContent = '';
    // Desktop Station Comms is already fully visible. Aligning it to `start`
    // scrolled the Station's own viewport by the header height and left the
    // large THE STATION title clipped behind the fixed navigation rail. Nearest
    // is a no-op there, while still revealing the stacked panel on compact
    // layouts without needlessly throwing away the route/departure context.
    this.panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    this.panel.focus({ preventScroll: true });
    await this.refreshOverview(true);
  }

  private async refreshOverview(force = false): Promise<void> {
    if (!this.available || this.overviewBusy || (!force && this.panel.offsetParent === null)) return;
    this.overviewBusy = true;
    try {
      this.overview = await this.service.overview();
      if (this.selected) this.selected = this.overview.friends.find((friend) => friend.userId === this.selected?.userId) ?? null;
      this.renderContacts();
      this.renderConversation();
    } catch (error) {
      this.showError(error);
    } finally {
      this.overviewBusy = false;
    }
  }

  private renderContacts(): void {
    this.requests.replaceChildren();
    for (const request of this.overview.incoming) {
      const row = document.createElement('li');
      const name = document.createElement('b');
      name.textContent = request.displayName;
      const accept = document.createElement('button');
      accept.type = 'button';
      accept.textContent = 'ACCEPT';
      accept.addEventListener('click', () => void this.accept(request));
      row.append(name, accept);
      this.requests.append(row);
    }
    for (const request of this.overview.outgoing) {
      const row = document.createElement('li');
      const name = document.createElement('b');
      name.textContent = request.displayName;
      const pending = document.createElement('small');
      pending.textContent = 'PENDING';
      row.append(name, pending);
      this.requests.append(row);
    }
    this.contacts.replaceChildren();
    for (const friend of this.overview.friends) {
      const presence = presentFriendPresence(friend);
      const button = document.createElement('button');
      button.type = 'button';
      button.classList.toggle('is-selected', friend.userId === this.selected?.userId);
      button.classList.toggle('has-unread', friend.unreadCount > 0);
      button.classList.toggle('is-online', presence.presence !== 'offline');
      button.classList.toggle('is-in-crew', presence.presence === 'crew');
      button.classList.toggle('is-in-queue', presence.presence === 'queue');
      button.classList.toggle('is-in-match', presence.presence === 'match');
      button.dataset.presence = presence.presence;
      button.setAttribute('aria-label', friend.unreadCount > 0
        ? `${friend.displayName}, ${presence.aria}, ${friend.unreadCount} unread ${friend.unreadCount === 1 ? 'signal' : 'signals'}`
        : `${friend.displayName}, ${presence.aria}`);
      const signal = document.createElement('i');
      const name = document.createElement('b');
      name.textContent = friend.displayName;
      const status = document.createElement('small');
      status.textContent = friend.unreadCount > 0
        ? friendUnreadStatus(friend, friend.unreadCount)
        : presence.status;
      button.append(signal, name, status);
      button.addEventListener('click', () => void this.select(friend));
      this.contacts.append(button);
    }
    this.renderUnreadTotal();
  }

  private renderUnreadTotal(): void {
    const summary = socialNotificationSummary(this.overview);
    for (const button of this.openButtons) {
      button.dataset.unread = String(summary.total);
      button.dataset.directUnread = String(summary.directMessages);
      button.dataset.friendRequests = String(summary.friendRequests);
      button.setAttribute('aria-label', summary.aria);
      const indicator = document.createElement('span');
      indicator.setAttribute('aria-hidden', 'true');
      indicator.textContent = '●';
      button.replaceChildren(document.createTextNode(summary.total > 0 ? `FRIENDS ${summary.total} ` : 'FRIENDS '), indicator);
    }
  }

  private async select(friend: FriendContact): Promise<void> {
    this.selected = friend;
    this.history = [];
    this.stopPolling();
    this.renderContacts();
    this.renderConversation();
    // Own the timer before yielding: another selection or logout must be able
    // to cancel it even while the initial history request is still in flight.
    this.polling = window.setInterval(() => void this.pollMessages(), 3_500);
    await this.pollMessages();
  }

  private renderConversation(followLatest = false): void {
    // Presence polling must not drag a reader away from older messages. New
    // channels begin at the latest message; explicit sends also resume following.
    const previousScrollTop = this.messages.scrollTop;
    const wasNearBottom = this.messages.scrollHeight - this.messages.clientHeight - previousScrollTop < 20;
    this.conversationTitle.textContent = this.selected?.displayName ?? 'SELECT A FRIEND';
    this.composer.disabled = !this.selected;
    this.sendButton.disabled = !this.selected || this.busy;
    this.removeButton.hidden = !this.selected;
    this.messages.replaceChildren();
    if (!this.selected) {
      const empty = document.createElement('p');
      empty.textContent = this.overview.friends.length ? 'SELECT A CREW LINK.' : 'ADD A FRIEND BY CALL TAG TO OPEN A SECURE CHANNEL.';
      this.messages.append(empty);
      return;
    }
    const myID = this.identity.current()?.id;
    for (const message of this.history) {
      const row = document.createElement('article');
      row.classList.toggle('is-mine', message.senderId === myID);
      const body = document.createElement('p');
      const inviteCode = crewInviteCode(message.body);
      if (inviteCode) {
        const label = document.createElement('span');
        label.textContent = 'PRIVATE CREW SIGNAL';
        const link = document.createElement('a');
        link.href = '/join/' + inviteCode;
        link.textContent = `JOIN CREW ${inviteCode} ↗`;
        body.classList.add('is-crew-invite');
        body.append(label, link);
      } else {
        body.textContent = message.body;
      }
      const time = document.createElement('time');
      time.dateTime = message.createdAt;
      time.textContent = formatMessageTime(message.createdAt);
      row.append(body, time);
      this.messages.append(row);
    }
    this.messages.scrollTop = followLatest || wasNearBottom
      ? this.messages.scrollHeight
      : Math.min(previousScrollTop, Math.max(0, this.messages.scrollHeight - this.messages.clientHeight));
  }

  private async pollMessages(): Promise<void> {
    const selectedID = this.selected?.userId;
    const ownerID = this.identity.current()?.id;
    if (!selectedID || this.busy || this.messagePollBusy || this.identity.current()?.guest !== false || this.panel.offsetParent === null) return;
    // Background reads must not consume a visible Send action. Keep their
    // in-flight guard separate from deliberate account/conversation writes.
    this.messagePollBusy = true;
    try {
      const after = this.history.at(-1)?.id ?? 0;
      const messages = await this.service.messages(selectedID, after);
      if (this.selected?.userId !== selectedID || this.identity.current()?.id !== ownerID || messages.length === 0) return;
      this.mergeHistory(messages);
      this.renderConversation();
      await this.refreshOverview();
    } catch (error) {
      this.showError(error);
    } finally {
      this.messagePollBusy = false;
    }
  }

  private async addFriend(): Promise<void> {
    if (this.busy || !this.friendCallTag.value.trim()) return;
    this.setBusy(true);
    try {
      await this.service.requestFriend(this.friendCallTag.value);
      this.friendCallTag.value = '';
      this.showError('FRIEND SIGNAL SENT.', false);
    } catch (error) {
      this.showError(error);
    } finally {
      this.setBusy(false);
      await this.refreshOverview();
    }
  }

  private async accept(friend: FriendContact): Promise<void> {
    try {
      await this.service.accept(friend.userId);
      await this.refreshOverview();
    } catch (error) {
      this.showError(error);
    }
  }

  private async send(): Promise<void> {
    const draft = this.composer.value;
    const body = draft.trim();
    const selectedID = this.selected?.userId;
    const ownerID = this.identity.current()?.id;
    if (!selectedID || !body || this.busy) return;
    this.setBusy(true);
    this.showError('', false);
    try {
      const message = await this.service.send(selectedID, body);
      if (this.selected?.userId !== selectedID || this.identity.current()?.id !== ownerID) return;
      // The composer remains available for the next draft while sending.
      if (this.composer.value === draft) this.composer.value = '';
      this.mergeHistory([message]);
      this.renderConversation(true);
    } catch (error) {
      this.showError(error);
    } finally {
      this.setBusy(false);
    }
  }

  private async removeSelected(): Promise<void> {
    if (!this.selected || this.busy) return;
    this.setBusy(true);
    try {
      await this.service.remove(this.selected.userId);
      this.selected = null;
      this.history = [];
      this.stopPolling();
      await this.refreshOverview();
    } catch (error) {
      this.showError(error);
    } finally {
      this.setBusy(false);
    }
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.sendButton.disabled = busy || !this.selected;
    this.addButton.disabled = busy;
    this.removeButton.disabled = busy || !this.selected;
  }

  private mergeHistory(messages: readonly DirectMessage[]): void {
    // A poll may contain a just-posted message before its POST response arrives,
    // or return older history after it. Either order yields one sorted record.
    const byID = new Map(this.history.map(message => [message.id, message]));
    for (const message of messages) byID.set(message.id, message);
    this.history = [...byID.values()].sort((left, right) => left.id-right.id);
  }

  private readonly stopPolling = (): void => {
    window.clearInterval(this.polling);
    this.polling = 0;
  };

  private startOverviewPolling(): void {
    this.stopOverviewPolling();
    this.overviewPolling = window.setInterval(() => void this.refreshOverview(), 2_500);
  }

  private stopOverviewPolling(): void {
    window.clearInterval(this.overviewPolling);
    this.overviewPolling = 0;
  }

  private showError(error: unknown, danger = true): void {
    this.error.textContent = error instanceof IdentityError ? error.message : String(error);
    this.error.dataset.danger = String(danger);
  }
}

function formatMessageTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

function required<T extends Element>(root: HTMLElement, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error('Missing social element: ' + selector);
  return element;
}
